// AdvancedRuleFrame.js
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Button, CircularProgress } from "@mui/material";
import NodeEditor from "./NodeEditor";
import NodeViewer from "./NodeViewer";
import { saveRuleset, loadRuleset } from "../reducers/rulesetSlice";
import { findNodeById } from "./nodeUtils";

const AdvancedRuleFrame = () => {
  const { root, nodeConfigs, loading, error, errors, success } = useSelector(
    (state) => state.ruleset,
  );

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  const has_advanced = useSelector(
    (state) => state.authen.loggedInUser?.advanced_licenses?.pathology,
  );

  const { selectedCategory } = useSelector((state) => state.ruleFrame);
  const [selectedPrefix, setSelectedPrefix] = useState([]);
  const dispatch = useDispatch();
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // Load ruleset on mount (or when category changes)
  useEffect(() => {
    if (license_key && selectedCategory) {
      dispatch(loadRuleset({ license_key, category: selectedCategory }));
      // clear selection when we load new ruleset
      setSelectedNodeId(null);
    }
  }, [dispatch, license_key, selectedCategory]);

  useEffect(() => {
    console.log("Root updated:", root);
  }, [root]);

  const handleSave = () => {
    dispatch(
      saveRuleset({ ruleset: root, license_key, category: selectedCategory }),
    );
  };

  const hasErrors = Object.values(errors || {}).some(
    (nodeErr) => nodeErr && Object.keys(nodeErr).length > 0,
  );

  // compute the node to edit when selectedNodeId is set
  const nodeToEdit = useMemo(() => {
    if (!selectedNodeId) return root;
    const found = findNodeById(root, selectedNodeId);
    return found || root;
  }, [root, selectedNodeId]);

  return (
    <Box>
      {/* Viewer (1/3) and Editor (2/3) side by side */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1 }}>
          <NodeViewer
            root={root}
            selectedCategory={selectedCategory}
            nodeConfigs={nodeConfigs}
            onSelectNode={({ id, numbering }) => {
              setSelectedNodeId(id);
              setSelectedPrefix(
                numbering
                  ? numbering.split(".").map((n) => parseInt(n, 10))
                  : [],
              );
            }}
            selectedNodeId={selectedNodeId}
          />
        </Box>

        <Box sx={{ flex: 2 }}>
          <NodeEditor
            node={nodeToEdit}
            selectedCategory={selectedCategory}
            nodeConfigs={nodeConfigs}
            prefix={selectedPrefix}
          />
        </Box>
      </Box>

      {/* Save button and error message below */}
      <Box
        sx={{
          mt: 2,
          textAlign: "right",
          pointerEvents: has_write && has_advanced ? "auto" : "none",
          opacity: has_write && has_advanced ? 1 : 0.2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading || hasErrors}
        >
          {loading ? <CircularProgress size={20} /> : "Save RuleSet"}
        </Button>
      </Box>
      {success && <Box sx={{ color: "green" }}>{success}</Box>}
      {error && <Box sx={{ color: "red" }}>{error}</Box>}
    </Box>
  );
};

export default AdvancedRuleFrame;
